import React, { useState, useEffect, useRef } from 'react'
import { apiGet } from '../../utils/api'
import { ZohoChat } from './ZohoChatsView'

/* ─── Types ──────────────────────────────────────────────────────────── */

interface ChatDetailPanelProps {
  chat: ZohoChat
}

interface ParsedMessage {
  type: 'message' | 'system'
  sender: string
  text: string
  timestamp: string
}

interface ChatDetail extends ZohoChat {
  raw_text?: string
  chunks_json?: string
}

/* ─── Raw-text parser ────────────────────────────────────────────────── */

function normalizeText(raw: string): string {
  return raw.replace(/\t/g, ' ').replace(/ {2,}/g, ' ')
}

const TIME_RE = /^\d{1,2}:\d{2}(:\d{2})?\s*(AM|PM)$/i
const DATE_TIME_RE = /^\d{1,2}\s+\w{3},?\s+\d{1,2}:\d{2}(:\d{2})?\s*(AM|PM)?$/i

function isTimestamp(line: string): boolean {
  const t = line.trim()
  return TIME_RE.test(t) || DATE_TIME_RE.test(t)
}

const SYSTEM_PHRASES = [
  'forwarded the chat request',
  'accepted the chat transfer',
  'has ended this chat',
  'has reopened this chat',
  'has left this chat',
  'ended this chat session',
  'transferred the chat',
  'joined this chat',
  'chat session timed out',
  'idle chat timeout',
]

function isSystemLine(line: string): boolean {
  const lower = line.toLowerCase()
  return SYSTEM_PHRASES.some((p) => lower.includes(p))
}

// Known bot / operator names (prefixes)
const BOT_NAMES = ['wequote', 'wequote whatsapp']

function parseRawTranscript(raw: string, visitorName?: string | null, operatorName?: string | null): ParsedMessage[] {
  const normalized = normalizeText(raw)
  const lines = normalized.split('\n')
  const messages: ParsedMessage[] = []

  // Find the "Chat Duration" line — real messages start after it
  let startIdx = 0
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].trim().toLowerCase().startsWith('chat duration')) {
      startIdx = i + 1
      break
    }
  }

  // Collect known sender names for matching
  const knownSenders = new Set<string>()
  if (visitorName) knownSenders.add(visitorName.trim().toLowerCase())
  if (operatorName) knownSenders.add(operatorName.trim().toLowerCase())
  BOT_NAMES.forEach((b) => knownSenders.add(b))

  // Parse messages: pattern is Sender, then message lines, then timestamp
  let currentSender = ''
  let currentLines: string[] = []
  let currentTimestamp = ''

  function flush() {
    const text = currentLines.join(' ').trim()
    if (!text && !currentSender) return

    if (isSystemLine(text)) {
      messages.push({
        type: 'system',
        sender: '',
        text,
        timestamp: currentTimestamp,
      })
    } else if (currentSender && text) {
      messages.push({
        type: 'message',
        sender: currentSender,
        text,
        timestamp: currentTimestamp,
      })
    }
    currentLines = []
    currentTimestamp = ''
  }

  function isSenderLine(line: string): boolean {
    const t = line.trim().toLowerCase()
    if (!t) return false
    // Exact match to known senders
    if (knownSenders.has(t)) return true
    // Short line (< 40 chars) that doesn't look like a message
    if (t.length < 40 && !t.includes('.') && !t.includes('?') && !t.includes('!') && !t.includes(',')) {
      // Check if it's a name-like pattern (capitalized words)
      const words = line.trim().split(/\s+/)
      if (words.length <= 3 && words.every((w) => /^[A-Z]/.test(w) || BOT_NAMES.some((b) => t.startsWith(b)))) {
        return true
      }
    }
    return false
  }

  for (let i = startIdx; i < lines.length; i++) {
    const line = lines[i]
    const trimmed = line.trim()

    // Skip empty lines
    if (!trimmed) continue

    // Timestamp line — attach to current message
    if (isTimestamp(trimmed)) {
      currentTimestamp = trimmed
      flush()
      currentSender = ''
      continue
    }

    // System message line
    if (isSystemLine(trimmed)) {
      flush()
      currentSender = ''
      currentLines = [trimmed]
      continue
    }

    // Potential sender line
    if (isSenderLine(trimmed) && currentLines.length === 0) {
      currentSender = trimmed
      continue
    }

    // Regular message content
    currentLines.push(trimmed)
  }

  // Flush remaining
  flush()

  return messages
}

/* ─── Sender classification ──────────────────────────────────────────── */

type SenderRole = 'bot' | 'visitor' | 'operator'

function classifySender(sender: string, visitorName?: string | null, operatorName?: string | null): SenderRole {
  const lower = sender.toLowerCase().trim()
  if (BOT_NAMES.some((b) => lower.startsWith(b))) return 'bot'
  if (operatorName && lower === operatorName.toLowerCase().trim()) return 'operator'
  return 'visitor'
}

/* ─── Avatar component ───────────────────────────────────────────────── */

const Avatar: React.FC<{ name: string; role: SenderRole; size?: 'sm' | 'md' }> = ({ name, role, size = 'md' }) => {
  const dim = size === 'sm' ? 'w-7 h-7 text-[10px]' : 'w-8 h-8 text-xs'

  if (role === 'bot') {
    return (
      <div className={`${dim} rounded-full bg-slate-800 text-white flex items-center justify-center flex-shrink-0`}>
        <svg className={size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4'} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2a2 2 0 012 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 017 7v1h1a1 1 0 110 2h-1v1a7 7 0 01-7 7H9a7 7 0 01-7-7v-1H1a1 1 0 110-2h1v-1a7 7 0 017-7h1V5.73A2 2 0 0112 2zM9 14a1 1 0 100 2 1 1 0 000-2zm6 0a1 1 0 100 2 1 1 0 000-2z"/>
        </svg>
      </div>
    )
  }

  const initials = name
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

  const colors = role === 'operator'
    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
    : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'

  return (
    <div className={`${dim} rounded-full ${colors} flex items-center justify-center flex-shrink-0 font-bold`}>
      {initials || 'U'}
    </div>
  )
}

/* ─── Main component ─────────────────────────────────────────────────── */

export const ChatDetailPanel: React.FC<ChatDetailPanelProps> = ({ chat }) => {
  const [detail, setDetail] = useState<ChatDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [viewMode, setViewMode] = useState<'conversation' | 'pdf'>('conversation')
  const [showInfo, setShowInfo] = useState(true)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setDetail(null)

    apiGet<ChatDetail>(`/zoho-chats/${chat.id}`)
      .then((data) => {
        if (!cancelled) {
          setDetail(data)
          setLoading(false)
        }
      })
      .catch((err) => {
        console.error('Failed to fetch chat detail:', err)
        if (!cancelled) setLoading(false)
      })

    return () => { cancelled = true }
  }, [chat.id])

  const displayChat = detail || chat
  const messages = detail?.raw_text
    ? parseRawTranscript(detail.raw_text, displayChat.visitor_name, displayChat.operator)
    : []

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-surface-secondary/20">
        <div className="flex flex-col items-center gap-3">
          <svg className="animate-spin w-8 h-8 text-brand-600" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span className="text-xs text-txt-secondary">Loading conversation...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex">
      {/* ─── Main conversation area ──────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header bar */}
        <div className="flex-shrink-0 px-5 py-3 border-b border-border bg-surface-primary flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <Avatar name={displayChat.visitor_name || 'U'} role="visitor" size="md" />
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-semibold text-txt-primary dark:text-white truncate">
                  {displayChat.visitor_name || 'Unknown Visitor'}
                </h2>
                <span className="text-[11px] font-mono text-txt-secondary bg-surface-tertiary px-1.5 py-0.5 rounded">
                  #{displayChat.chat_number}
                </span>
              </div>
              {displayChat.visitor_email && (
                <p className="text-[11px] text-txt-secondary truncate">{displayChat.visitor_email}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* View mode toggle */}
            <div className="flex items-center bg-surface-secondary rounded-lg p-0.5">
              <button
                onClick={() => setViewMode('conversation')}
                className={`px-3 py-1 text-[11px] font-medium rounded-md transition-all ${
                  viewMode === 'conversation'
                    ? 'bg-white dark:bg-surface-primary text-txt-primary dark:text-white shadow-sm'
                    : 'text-txt-secondary hover:text-txt-primary'
                }`}
              >
                Conversation
              </button>
              <button
                onClick={() => setViewMode('pdf')}
                className={`px-3 py-1 text-[11px] font-medium rounded-md transition-all ${
                  viewMode === 'pdf'
                    ? 'bg-white dark:bg-surface-primary text-txt-primary dark:text-white shadow-sm'
                    : 'text-txt-secondary hover:text-txt-primary'
                }`}
              >
                PDF
              </button>
            </div>

            {/* Info panel toggle */}
            <button
              onClick={() => setShowInfo(!showInfo)}
              className={`p-1.5 rounded-md transition-colors ${
                showInfo ? 'bg-brand-600/10 text-brand-600' : 'text-txt-secondary hover:bg-surface-secondary'
              }`}
              title="Toggle visitor info"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
              </svg>
            </button>
          </div>
        </div>

        {/* Conversation or PDF content */}
        <div className="flex-1 overflow-y-auto">
          {viewMode === 'conversation' ? (
            <ConversationView
              messages={messages}
              chat={displayChat}
              rawText={detail?.raw_text}
              messagesEndRef={messagesEndRef}
            />
          ) : (
            <PdfViewer chatNumber={displayChat.chat_number} />
          )}
        </div>
      </div>

      {/* ─── Visitor info sidebar ────────────────────────────── */}
      {showInfo && (
        <div className="w-[280px] flex-shrink-0 border-l border-border bg-surface-primary overflow-y-auto">
          <VisitorInfoPanel chat={displayChat} />
        </div>
      )}
    </div>
  )
}

/* ─── Conversation View ──────────────────────────────────────────────── */

const ConversationView: React.FC<{
  messages: ParsedMessage[]
  chat: ChatDetail | ZohoChat
  rawText?: string
  messagesEndRef: React.RefObject<HTMLDivElement>
}> = ({ messages, chat, rawText, messagesEndRef }) => {
  if (messages.length === 0 && rawText) {
    // Fallback: couldn't parse, show formatted raw text
    return (
      <div className="p-5">
        <div className="bg-surface-secondary/50 rounded-xl p-4 border border-border/50">
          <pre className="text-[13px] text-txt-primary dark:text-white whitespace-pre-wrap font-sans leading-relaxed">
            {normalizeText(rawText)}
          </pre>
        </div>
      </div>
    )
  }

  if (messages.length === 0) {
    return (
      <div className="p-6 text-sm text-txt-secondary text-center mt-8">
        No transcript data available. Try the PDF view instead.
      </div>
    )
  }

  // Group sequential messages from the same sender
  const grouped: { sender: string; role: SenderRole; items: ParsedMessage[] }[] = []
  for (const msg of messages) {
    if (msg.type === 'system') {
      grouped.push({ sender: '', role: 'bot', items: [msg] })
    } else {
      const role = classifySender(msg.sender, chat.visitor_name, (chat as any).operator)
      const last = grouped[grouped.length - 1]
      if (last && last.sender === msg.sender && last.items[0].type !== 'system') {
        last.items.push(msg)
      } else {
        grouped.push({ sender: msg.sender, role, items: [msg] })
      }
    }
  }

  return (
    <div className="px-5 py-4 space-y-4 bg-[#f8f9fb] dark:bg-surface-secondary/20 min-h-full">
      {/* Chat started indicator */}
      <div className="flex justify-center">
        <span className="text-[10px] text-txt-secondary bg-white dark:bg-surface-primary border border-border/60 px-3 py-1 rounded-full shadow-sm">
          Chat #{chat.chat_number} &middot; {chat.chat_date ? new Date(chat.chat_date).toLocaleDateString('en-GB', {
            day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
          }) : 'Unknown date'}
        </span>
      </div>

      {grouped.map((group, gIdx) => {
        // System message
        if (group.items[0].type === 'system') {
          return (
            <div key={gIdx} className="flex justify-center">
              <span className="text-[11px] text-txt-secondary italic bg-white/80 dark:bg-surface-primary/60 border border-border/40 px-3 py-1 rounded-full">
                {group.items[0].text}
                {group.items[0].timestamp && (
                  <span className="ml-2 text-[10px] text-txt-secondary/60">{group.items[0].timestamp}</span>
                )}
              </span>
            </div>
          )
        }

        const isBot = group.role === 'bot'

        return (
          <div key={gIdx} className="flex items-start gap-2.5">
            <Avatar name={group.sender} role={group.role} size="sm" />
            <div className="flex-1 min-w-0 max-w-[80%]">
              {/* Sender name */}
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-[11px] font-semibold ${
                  isBot ? 'text-slate-600 dark:text-slate-300'
                    : group.role === 'operator' ? 'text-emerald-700 dark:text-emerald-400'
                    : 'text-blue-700 dark:text-blue-400'
                }`}>
                  {group.sender}
                </span>
                {isBot && (
                  <span className="text-[9px] bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400 px-1.5 py-px rounded font-medium uppercase tracking-wide">
                    Bot
                  </span>
                )}
                {group.role === 'operator' && (
                  <span className="text-[9px] bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 px-1.5 py-px rounded font-medium uppercase tracking-wide">
                    Agent
                  </span>
                )}
              </div>

              {/* Message bubbles */}
              <div className="space-y-1">
                {group.items.map((msg, mIdx) => (
                  <div key={mIdx}>
                    <div className={`inline-block rounded-2xl px-3.5 py-2 text-[13px] leading-relaxed ${
                      isBot
                        ? 'bg-slate-200/80 dark:bg-slate-700/60 text-txt-primary dark:text-white rounded-tl-md'
                        : group.role === 'operator'
                          ? 'bg-white dark:bg-surface-primary text-txt-primary dark:text-white border border-border/60 shadow-sm rounded-tl-md'
                          : 'bg-blue-500 text-white rounded-tl-md'
                    }`}>
                      {msg.text}
                    </div>
                    {msg.timestamp && (
                      <div className="mt-0.5 text-[10px] text-txt-secondary/60 pl-1">
                        {msg.timestamp}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )
      })}

      {/* Chat completed badge */}
      <div className="flex justify-center pt-2 pb-4">
        <div className="flex items-center gap-2 text-[11px] text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/40 px-4 py-1.5 rounded-full font-medium">
          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
          </svg>
          CHAT COMPLETED
        </div>
      </div>

      <div ref={messagesEndRef} />
    </div>
  )
}

/* ─── Visitor info sidebar ───────────────────────────────────────────── */

const VisitorInfoPanel: React.FC<{ chat: ChatDetail | ZohoChat }> = ({ chat }) => {
  const initials = (chat.visitor_name || 'U')
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

  return (
    <div className="px-4 py-5">
      {/* Visitor card */}
      <div className="flex flex-col items-center text-center mb-5">
        <div className="w-14 h-14 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 flex items-center justify-center text-lg font-bold mb-2">
          {initials}
        </div>
        <h3 className="text-sm font-semibold text-txt-primary dark:text-white">
          {chat.visitor_name || 'Unknown Visitor'}
        </h3>
        {chat.visitor_email && (
          <a href={`mailto:${chat.visitor_email}`} className="text-[11px] text-brand-600 hover:underline mt-0.5">
            {chat.visitor_email}
          </a>
        )}
        {chat.city && (
          <p className="text-[11px] text-txt-secondary mt-0.5">
            {[chat.city, chat.state, chat.country].filter(Boolean).join(', ')}
          </p>
        )}
      </div>

      <div className="border-t border-border pt-4 space-y-4">
        {/* Conversation Info */}
        <div>
          <h4 className="text-[11px] font-semibold text-txt-secondary uppercase tracking-wider mb-2.5">
            Conversation Info
          </h4>
          <dl className="space-y-2">
            <InfoRow label="Chat #" value={`#${chat.chat_number}`} />
            {chat.chat_date && (
              <InfoRow
                label="Date"
                value={new Date(chat.chat_date).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              />
            )}
            {chat.department && <InfoRow label="Department" value={chat.department} />}
            {chat.operator && <InfoRow label="Operator" value={chat.operator} />}
            {chat.message_count > 0 && <InfoRow label="Messages" value={String(chat.message_count)} />}
            <InfoRow label="Status" value={chat.status || 'imported'} badge />
          </dl>
        </div>

        {/* Visitor Details */}
        {(chat.os || chat.browser || chat.device || chat.website) && (
          <div className="border-t border-border pt-4">
            <h4 className="text-[11px] font-semibold text-txt-secondary uppercase tracking-wider mb-2.5">
              Visitor Details
            </h4>
            <dl className="space-y-2">
              {chat.website && <InfoRow label="Website" value={chat.website} link />}
              {chat.os && <InfoRow label="OS" value={chat.os} />}
              {chat.browser && <InfoRow label="Browser" value={chat.browser} />}
              {chat.device && <InfoRow label="Device" value={chat.device} />}
            </dl>
          </div>
        )}

        {/* Location */}
        {(chat.city || chat.country) && (
          <div className="border-t border-border pt-4">
            <h4 className="text-[11px] font-semibold text-txt-secondary uppercase tracking-wider mb-2.5">
              Location
            </h4>
            <dl className="space-y-2">
              {chat.city && <InfoRow label="City" value={chat.city} />}
              {chat.state && <InfoRow label="State" value={chat.state} />}
              {chat.country && <InfoRow label="Country" value={chat.country} />}
            </dl>
          </div>
        )}
      </div>
    </div>
  )
}

const InfoRow: React.FC<{ label: string; value: string; badge?: boolean; link?: boolean }> = ({ label, value, badge, link }) => (
  <div className="flex items-start justify-between gap-2">
    <dt className="text-[11px] text-txt-secondary flex-shrink-0">{label}</dt>
    <dd className="text-[11px] text-txt-primary dark:text-white font-medium text-right truncate max-w-[160px]">
      {badge ? (
        <span className="inline-block bg-brand-600/10 text-brand-600 px-1.5 py-px rounded text-[10px] font-semibold uppercase">
          {value}
        </span>
      ) : link ? (
        <a href={value} target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">
          {new URL(value).hostname}
        </a>
      ) : (
        value
      )}
    </dd>
  </div>
)

/* ─── PDF Viewer ─────────────────────────────────────────────────────── */

const PdfViewer: React.FC<{ chatNumber: number }> = ({ chatNumber }) => {
  const token = localStorage.getItem('auth_token') || ''
  const pdfUrl = `/api/zoho-chats/${chatNumber}/pdf?token=${encodeURIComponent(token)}`

  return (
    <div className="h-full">
      <iframe
        src={pdfUrl}
        className="w-full h-full border-0"
        title={`Chat #${chatNumber} PDF`}
      />
    </div>
  )
}
